// Ground heightmap, surface mask and static obstacles. Pure TypeScript: shared by the browser game,
// CPU bots and the headless AI player. Coordinates are Babylon (x right, y up, z forward).

export const Surface = { none: 0, grass: 1, road: 2, pavement: 3, apron: 4, rail: 5, sand: 6 } as const;
export type SurfaceType = (typeof Surface)[keyof typeof Surface];

export interface Obstacle {
  k: 'box' | 'circle';
  m: string;
  /** min corner y, centre x/z */
  p: [number, number, number];
  /** half extents x, z (for circles both are the radius) */
  h: [number, number];
  /** degrees, Babylon yaw */
  yaw: number;
  top: number;
  // cached
  cos?: number;
  sin?: number;
}

export interface GroundHeader {
  origin: [number, number];
  cell: number;
  cols: number;
  rows: number;
  blocks?: { cell: number; cols: number; rows: number };
  obstacles: Obstacle[];
}

export class Ground {
  readonly origin: [number, number];
  readonly cell: number;
  readonly cols: number;
  readonly rows: number;
  readonly heights: Float32Array;
  readonly types: Uint8Array;
  readonly obstacles: Obstacle[];
  /** fine occupancy grid: 1 = solid at car height; tops in half metres above ground */
  readonly bcell: number; readonly bcols: number; readonly brows: number;
  readonly blocked: Uint8Array; readonly tops: Uint8Array;
  private readonly hash = new Map<number, Obstacle[]>();
  private readonly hashCell = 20;

  constructor(header: GroundHeader, bin: ArrayBuffer, blocks?: ArrayBuffer) {
    this.origin = header.origin;
    this.cell = header.cell;
    this.cols = header.cols;
    this.rows = header.rows;
    const n = this.cols * this.rows;
    this.heights = new Float32Array(bin, 0, n);
    this.types = new Uint8Array(bin, n * 4, n);
    this.obstacles = header.obstacles;
    const b = header.blocks ?? { cell: this.cell, cols: this.cols, rows: this.rows };
    this.bcell = b.cell; this.bcols = b.cols; this.brows = b.rows;
    const bn = b.cols * b.rows;
    if (blocks && blocks.byteLength >= bn * 2) { this.blocked = new Uint8Array(blocks, 0, bn); this.tops = new Uint8Array(blocks, bn, bn); }
    else { this.blocked = new Uint8Array(bn); this.tops = new Uint8Array(bn); }
    for (const o of this.obstacles) {
      const r = (o.yaw * Math.PI) / 180;
      o.cos = Math.cos(r);
      o.sin = Math.sin(r);
      const reach = Math.hypot(o.h[0], o.h[1]);
      const x0 = Math.floor((o.p[0] - reach) / this.hashCell), x1 = Math.floor((o.p[0] + reach) / this.hashCell);
      const z0 = Math.floor((o.p[2] - reach) / this.hashCell), z1 = Math.floor((o.p[2] + reach) / this.hashCell);
      for (let gx = x0; gx <= x1; gx++)
        for (let gz = z0; gz <= z1; gz++) {
          const key = gx * 100003 + gz;
          let list = this.hash.get(key);
          if (!list) this.hash.set(key, (list = []));
          list.push(o);
        }
    }
  }

  /**
   * Fetch ground.json + ground.bin (+ blocks.bin). `version` goes on the query string so CDN caches (Cloudflare
   * caches .bin for hours but never .json) cannot pair a new header with an old binary; sizes are checked and
   * a mismatch is retried once with a cache-busting query before giving up with a readable error.
   */
  static async load(baseUrl: string, fetchImpl: typeof fetch = fetch, version = ''): Promise<Ground> {
    const get = async (q: string) => {
      const [header, bin, blocks] = await Promise.all([
        fetchImpl(`${baseUrl}ground.json${q}`).then((r) => r.json() as Promise<GroundHeader>),
        fetchImpl(`${baseUrl}ground.bin${q}`).then((r) => r.arrayBuffer()),
        fetchImpl(`${baseUrl}blocks.bin${q}`).then((r) => (r.ok ? r.arrayBuffer() : undefined)).catch(() => undefined),
      ]);
      const ok = bin.byteLength === header.cols * header.rows * 5 && (!blocks || !header.blocks || blocks.byteLength === header.blocks.cols * header.blocks.rows * 2);
      return { header, bin, blocks, ok };
    };
    let r = await get(version ? `?v=${encodeURIComponent(version)}` : '');
    if (!r.ok) r = await get(`?v=${encodeURIComponent(version || 'x')}&r=${Date.now()}`);
    if (!r.ok) throw new Error(`Level data mismatch (ground ${r.bin.byteLength} B for ${r.header.cols}x${r.header.rows}); reload the page`);
    return new Ground(r.header, r.bin, r.blocks);
  }

  private idx(c: number, r: number): number {
    c = Math.max(0, Math.min(this.cols - 1, c));
    r = Math.max(0, Math.min(this.rows - 1, r));
    return r * this.cols + c;
  }

  /** Bilinear ground height. Cells with no surface (water) read as -1. */
  heightAt(x: number, z: number): number {
    const fx = (x - this.origin[0]) / this.cell - 0.5;
    const fz = (z - this.origin[1]) / this.cell - 0.5;
    const c0 = Math.floor(fx), r0 = Math.floor(fz);
    const tx = fx - c0, tz = fz - r0;
    const h00 = this.heights[this.idx(c0, r0)], h10 = this.heights[this.idx(c0 + 1, r0)];
    const h01 = this.heights[this.idx(c0, r0 + 1)], h11 = this.heights[this.idx(c0 + 1, r0 + 1)];
    return (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
  }

  surfaceAt(x: number, z: number): SurfaceType {
    const c = Math.floor((x - this.origin[0]) / this.cell);
    const r = Math.floor((z - this.origin[1]) / this.cell);
    if (c < 0 || r < 0 || c >= this.cols || r >= this.rows) return Surface.none;
    return this.types[r * this.cols + c] as SurfaceType;
  }

  /** Is the fine cell containing x,z solid at car height? */
  blockedAt(x: number, z: number): boolean {
    const c = Math.floor((x - this.origin[0]) / this.bcell), r = Math.floor((z - this.origin[1]) / this.bcell);
    if (c < 0 || r < 0 || c >= this.bcols || r >= this.brows) return false;
    return this.blocked[r * this.bcols + c] === 1;
  }
  /** Height of the solid thing in a fine cell, metres above ground (0 when free). */
  topAt(x: number, z: number): number {
    const c = Math.floor((x - this.origin[0]) / this.bcell), r = Math.floor((z - this.origin[1]) / this.bcell);
    if (c < 0 || r < 0 || c >= this.bcols || r >= this.brows) return 0;
    return this.tops[r * this.bcols + c] / 2;
  }
  /** A substantial solid wall between a car and a hazard. Sample across the car's
   * width so a lamp post or a wall clipping one corner cannot count as shelter. */
  shelteredFrom(x: number, z: number, sourceX: number, sourceZ: number, y: number, maxDistance = 24): boolean {
    const length = Math.hypot(sourceX - x, sourceZ - z);
    if (length < 2) return false;
    const dx = (sourceX - x) / length, dz = (sourceZ - z) / length;
    const step = Math.min(this.bcell * 0.5, 0.5);
    return [-1, 0, 1].every((side) => {
      let solid = 0;
      for (let d = 1.5; d < Math.min(length, maxDistance); d += step) {
        const sx = x + dx * d - dz * side, sz = z + dz * d + dx * side;
        if (this.blockedAt(sx, sz) && this.topAt(sx, sz) >= 3.5 && this.heightAt(sx, sz) + this.topAt(sx, sz) > y + 2.5) {
          solid += step; if (solid >= 2) return true;
        } else solid = 0;
      }
      return false;
    });
  }

  /** Metres of unobstructed road ahead from x,z along yaw (up to maxDist), used to pick spawn directions. */
  clearRun(x: number, z: number, yaw: number, maxDist = 40): number {
    const fx = Math.sin(yaw), fz = Math.cos(yaw);
    for (let d = 1; d <= maxDist; d += 1) {
      const px = x + fx * d, pz = z + fz * d;
      if (this.blockedNear(px, pz, 1.4) || !this.isRoad(px, pz)) return d - 1;
    }
    return maxDist;
  }

  /** Best road cell near x,z to start a car from: the closest one with a long clear run along the road. */
  bestSpawnNear(x: number, z: number, radius = 70): { x: number; z: number; yaw: number } | null {
    let best: { x: number; z: number; yaw: number; score: number } | null = null;
    const c0 = Math.floor((x - radius - this.origin[0]) / this.cell), c1 = Math.floor((x + radius - this.origin[0]) / this.cell);
    const r0 = Math.floor((z - radius - this.origin[1]) / this.cell), r1 = Math.floor((z + radius - this.origin[1]) / this.cell);
    for (let r = Math.max(0, r0); r <= Math.min(this.rows - 1, r1); r += 2) for (let c = Math.max(0, c0); c <= Math.min(this.cols - 1, c1); c += 2) {
      if (this.types[r * this.cols + c] !== Surface.road) continue;
      const cx = this.origin[0] + (c + 0.5) * this.cell, cz = this.origin[1] + (r + 0.5) * this.cell;
      const d = Math.hypot(cx - x, cz - z); if (d > radius || this.blockedNear(cx, cz, 2.2)) continue;
      const axis = this.roadHeading(cx, cz);
      const a = this.clearRun(cx, cz, axis, 60), b = this.clearRun(cx, cz, axis + Math.PI, 60);
      const run = Math.max(a, b), back = Math.min(a, b);
      if (run < 25) continue;
      // long run ahead, some room behind for staggered cars, and close to the rank
      const score = Math.min(run, 60) + Math.min(back, 20) * 0.5 - d * 0.35;
      if (!best || score > best.score) best = { x: cx, z: cz, yaw: a >= b ? axis : axis + Math.PI, score };
    }
    return best ? { x: best.x, z: best.z, yaw: best.yaw } : null;
  }

  /** Any blocked fine cell within radius of x,z (cheap square test). */
  blockedNear(x: number, z: number, radius: number): boolean {
    const c0 = Math.floor((x - radius - this.origin[0]) / this.bcell), c1 = Math.floor((x + radius - this.origin[0]) / this.bcell);
    const r0 = Math.floor((z - radius - this.origin[1]) / this.bcell), r1 = Math.floor((z + radius - this.origin[1]) / this.bcell);
    for (let r = Math.max(0, r0); r <= Math.min(this.brows - 1, r1); r++) for (let c = Math.max(0, c0); c <= Math.min(this.bcols - 1, c1); c++) if (this.blocked[r * this.bcols + c]) return true;
    return false;
  }

  isRoad(x: number, z: number): boolean {
    const s = this.surfaceAt(x, z);
    return s === Surface.road || s === Surface.apron;
  }

  /** Nearest road cell centre to x,z (spiral search), or null if none within maxRadius metres. */
  nearestRoad(x: number, z: number, maxRadius = 60): { x: number; z: number } | null {
    const c0 = Math.floor((x - this.origin[0]) / this.cell), r0 = Math.floor((z - this.origin[1]) / this.cell);
    const maxRing = Math.ceil(maxRadius / this.cell);
    for (let ring = 0; ring <= maxRing; ring++) {
      let best: { x: number; z: number; d: number } | null = null;
      for (let dr = -ring; dr <= ring; dr++) for (let dc = -ring; dc <= ring; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== ring) continue;
        const c = c0 + dc, r = r0 + dr;
        if (c < 0 || r < 0 || c >= this.cols || r >= this.rows) continue;
        if (this.types[r * this.cols + c] !== Surface.road) continue;
        const cx = this.origin[0] + (c + 0.5) * this.cell, cz = this.origin[1] + (r + 0.5) * this.cell;
        if (this.blockedNear(cx, cz, 1.6)) continue;
        const d = Math.hypot(cx - x, cz - z);
        if (!best || d < best.d) best = { x: cx, z: cz, d };
      }
      if (best) return { x: best.x, z: best.z };
    }
    return null;
  }

  /** Heading (radians, Babylon yaw) of the road at x,z: the axis along which road cells extend furthest. */
  roadHeading(x: number, z: number): number {
    const run = (dx: number, dz: number) => { let n = 0; while (n < 20 && this.isRoad(x + dx * n * this.cell, z + dz * n * this.cell)) n++; return n; };
    const alongZ = run(0, 1) + run(0, -1), alongX = run(1, 0) + run(-1, 0);
    return alongZ >= alongX ? 0 : Math.PI / 2;
  }

  /** Obstacles whose hash cells overlap the circle at x,z with radius r. */
  nearby(x: number, z: number, r: number): Obstacle[] {
    const out: Obstacle[] = [];
    const seen = new Set<Obstacle>();
    const x0 = Math.floor((x - r) / this.hashCell), x1 = Math.floor((x + r) / this.hashCell);
    const z0 = Math.floor((z - r) / this.hashCell), z1 = Math.floor((z + r) / this.hashCell);
    for (let gx = x0; gx <= x1; gx++)
      for (let gz = z0; gz <= z1; gz++) {
        const list = this.hash.get(gx * 100003 + gz);
        if (!list) continue;
        for (const o of list) if (!seen.has(o)) { seen.add(o); out.push(o); }
      }
    return out;
  }
}
